import type { IncomingHttpHeaders } from 'http';
import type { NextApiRequest } from 'next';

/** Default SameSite for Hub first-party cookies (matches prior `cookies-next` / Amplify behavior). */
export const HUB_COOKIE_SAMESITE = 'strict' as const;
const COOKIE_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

/**
 * True when the request is served over HTTPS (e.g. behind a TLS-terminating proxy).
 */
export function isHttpsRequest(
  req:
    | Pick<NextApiRequest, 'headers'>
    | { headers?: IncomingHttpHeaders }
    | null
    | undefined
): boolean {
  if (!req?.headers) return false;
  const raw = req.headers['x-forwarded-proto'];
  const proto = Array.isArray(raw) ? raw[0] : raw;
  if (
    typeof proto === 'string' &&
    proto.split(',')[0]?.trim().toLowerCase() === 'https'
  ) {
    return true;
  }
  const rawSsl = req.headers['x-forwarded-ssl'];
  const ssl = Array.isArray(rawSsl) ? rawSsl[0] : rawSsl;
  if (
    typeof ssl === 'string' &&
    ssl.split(',')[0]?.trim().toLowerCase() === 'on'
  ) {
    return true;
  }
  return false;
}

/**
 * Options for `cookies-next` when called from the browser only.
 * HttpOnly cannot be set from JavaScript; use server helpers for HttpOnly cookies.
 */
export function getClientCookieOptions(): {
  sameSite: 'strict';
  secure: boolean;
} {
  if (typeof window === 'undefined') {
    return { sameSite: HUB_COOKIE_SAMESITE, secure: false };
  }
  return {
    sameSite: HUB_COOKIE_SAMESITE,
    secure: window.location.protocol === 'https:',
  };
}

export type AmplifyCookieStorageShape = {
  path: string;
  expires: number;
  sameSite: 'strict';
  secure: boolean;
};

/**
 * Amplify Auth `cookieStorage` options (browser). Cognito tokens remain
 * readable to JS (not HttpOnly); see docs/SECURITY_COOKIES.md.
 *
 * Host-only (no `domain`): avoids a second cookie lineage when server/Next
 * writes omit Domain while the client used Domain=hostname.
 */
export function getAmplifyCookieStorageOptions(
  hostWithoutPort: string
): AmplifyCookieStorageShape {
  const isLocal =
    hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1';
  return {
    path: '/',
    expires: 365,
    sameSite: HUB_COOKIE_SAMESITE,
    secure: !isLocal,
  };
}

/**
 * Build `Set-Cookie` attribute parts for server-issued session cookies (HttpOnly + explicit SameSite).
 * Defaults to **Strict**; use `sameSite: 'lax'` only where a cookie historically used Lax (e.g. `dev_session`).
 */
export function buildHttpOnlySessionCookieParts(params: {
  name: string;
  value: string;
  maxAgeSeconds: number;
  req: Pick<NextApiRequest, 'headers'>;
  sameSite?: 'strict' | 'lax';
}): string[] {
  const { name, value, maxAgeSeconds, req, sameSite = 'strict' } = params;
  if (!COOKIE_NAME_PATTERN.test(name)) {
    throw new Error(`Invalid cookie name: ${name}`);
  }
  const encodedValue = encodeURIComponent(value);
  const sameSiteHeader =
    sameSite === 'lax' ? 'SameSite=Lax' : 'SameSite=Strict';
  const parts = [
    `${name}=${encodedValue}`,
    'Path=/',
    'HttpOnly',
    sameSiteHeader,
    `Max-Age=${maxAgeSeconds}`,
  ];
  if (isHttpsRequest(req)) {
    parts.push('Secure');
  }
  return parts;
}
